import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LazyMediaDirective } from '../../shared/directives/lazy-media.directive';
import { SwipeDirective } from '../../shared/directives/swipe.directive';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ScrollToTop } from '../../components/common/scroll-to-top/scroll-to-top';
import { Subscription } from 'rxjs';
import { inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import {
  BlogService,
  IBlog,
  IBlogBlock,
  ReactionType,
} from '../../shared/services/blog.service';
// import { ReactionSoundService } from '../../shared/services/reaction-sound.service';
import { Modal } from '../../components/ui/modal/modal';
import { RelationshipService } from '../../shared/services/relationship.service';
import { FriendRequestService, FriendshipStatus } from '../../shared/services/friend-request.service';
import { ConfirmationPopup } from '../../components/ui/confirmation-popup/confirmation-popup';
import { AuthStateService } from '../../shared/states/auth.state';
import { FavouriteService } from '../../shared/services/favourites.service';
import { ShareSheetComponent } from '../../components/ui/share-sheet/share-sheet';
import { ShareConfig } from '../../shared/services/share.service';
import { SearchService } from '../../shared/services/search.service';
import { FriendRequestLimitPopup } from '../../components/ui/friend-request-limit-popup/friend-request-limit-popup';
import { ReportModal } from '../../components/ui/report-modal/report-modal';
import { ReportService } from '../../shared/services/report.service';
import { BlockUserConfirm } from '../../components/ui/block-user-confirm/block-user-confirm';

export interface IBlogVM {
  _id: string;
  userId: string;
  avatar: string;
  author: string;
  title: string;
  timeAgo: string;
  images: string[];
  mediaBlocks: IBlogBlock[];
  activeSlide: number;
  imageAspect: string;
  text: string;
  hasMore: boolean;
  likes: number;
  comments: number;
  shares: number;
  myReaction: ReactionType | null;
  reactions: number;
  totalReactions: number;
  reactionCounts: ReactionCounts;
  recentUserName: string | null;
  topTypes: ReactionType[];
  showEmojiPicker: boolean;
  hoveredReaction: ReactionType | null;
  isFollowed: boolean;
  isMyBlog: boolean;
  status: 'DRAFT' | 'PUBLISHED';
  commentsDisabled: boolean;
  friendshipStatus: FriendshipStatus;
  friendshipRequestId: string | null;
  isFavourite: boolean;
  /** Sequence index assigned by the "All" page mixed feed for CSS `order`. */
  globalOrder?: number;
  acceptedCollaborators: Array<{
    _id: string;
    displayName: string;
    userName: string;
    profileImage?: { urlSmall?: string; urlMedium?: string; urlLarge?: string } | null;
  }>;
  mentions: Array<{
    _id: string;
    displayName: string;
    userName: string;
    profileImage?: { urlSmall?: string; urlMedium?: string; urlLarge?: string } | null;
  }>;
  /** True only when this blog was removed by an admin after a report — the
   *  backend only ever includes a removed blog in the response when the
   *  viewer is its own author. */
  isRemovedByAdmin: boolean;
}

export const REACTIONS: {
  type: ReactionType;
  emoji: string;
  label: string;
  color: string;
  bg: string;
}[] = [
    { type: 'LIKE', emoji: '👍', label: 'Like', color: '#2078f4', bg: '#e7f3ff' },
    { type: 'HEART', emoji: '❤️', label: 'Love', color: '#f33e58', bg: '#ffe7eb' },
    { type: 'HUG', emoji: '🤗', label: 'Hug', color: '#f7b125', bg: '#fff4e0' },
    { type: 'WOW', emoji: '😮', label: 'Wow', color: '#f7b125', bg: '#fff4e0' },
    { type: 'SAD', emoji: '😢', label: 'Sad', color: '#7c93a3', bg: '#eef2f5' },
    { type: 'HAHA', emoji: '😂', label: 'Haha', color: '#f7b125', bg: '#fff4e0' },
  ];

interface ReactionCounts {
  LIKE: number; HEART: number; HUG: number;
  WOW: number; SAD: number; HAHA: number;
}

@Component({
  selector: 'app-blog',
  standalone: true,
  imports: [CommonModule, ScrollToTop, Modal, RouterModule, ConfirmationPopup, ShareSheetComponent, LazyMediaDirective, FriendRequestLimitPopup, SwipeDirective, ReportModal, BlockUserConfirm],
  templateUrl: './blog.html',
  styles: [':host { display: contents; }'],
})
export class Blog implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() userName?: string;
  @Input() embedded = false;
  @Input() featured = false;
  /** Home page only: lay cards out in an equal-height grid instead of
   *  masonry (fixed media frame, clamped text, footer pinned to the bottom).
   *  Off everywhere else, so every other page keeps the masonry layout. */
  @Input() uniform = false;
  @Input() showStatusFilter = false;   // true only on own profile blog tab
  /** When true (profile page toggle), only shows blogs where `userName` is an
   *  accepted collaborator rather than the author. */
  @Input() collabOnly = false;
  @Input() favouritesMode = false;
  @Input() gridCols = 0;
  /** When set (header search box / "All" page), fetches global search results
   *  (title match) instead of the normal feed. */
  @Input() searchQuery?: string;
  /** When set (user clicked a #hashtag), fetches only blogs carrying that tag. */
  @Input() searchHashtag?: string;

  /** When provided (by the "All" page mixed feed), renders these pre-fetched
   *  items instead of fetching its own. */
  @Input() mixedItems?: any[];
  /** True when embedded in the "All" page mixed feed. */
  @Input() mixedMode = false;

  @ViewChildren('cardRef') cardRefs!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('masonryGrid') masonryGrid!: ElementRef<HTMLElement>;
  @ViewChild('scrollSentinel') sentinelRef!: ElementRef<HTMLElement>;

  allBlogs: IBlogVM[] = [];   // full unfiltered list
  blogs: IBlogVM[] = [];
  statusFilter: 'ALL' | 'PUBLISHED' | 'DRAFT' = 'ALL';
  isLoading = false;
  hasError = false;
  page = 1;
  limit = 10;
  total = 0;
  readonly REACTIONS = REACTIONS;

  isReactionOpen = false;
  reactionData: any = null;
  reactionsLoading = false;
  activeReactionFilter: ReactionType | null = null;
  private activeReactionEventId: string | null = null;
  showCollabModal = false;
  selectedBlog: IBlogVM | null = null;
  showTaggedModal = false;

  private resizeObserver?: ResizeObserver;
  private cardResizeObserver?: ResizeObserver;
  private scrollObserver?: IntersectionObserver;
  private cardChangeSub?: Subscription;
  private refreshSub?: Subscription;
  private relationshipService = inject(RelationshipService);
  private friendRequestService = inject(FriendRequestService);
  private authState = inject(AuthStateService);
  private favouriteService = inject(FavouriteService);

  // ── Share sheet ──────────────────────────────────────────────────────────
  activeShareItem: ShareConfig | null = null;
  activeShareCount = 0;
  private activeShareRef: any = null;


  selectedPost: Blog | null = null;

  // ── Friend request cooldown popup state ────────────────────────────────────
  showFriendLimitPopup = false;
  friendLimitHours: number | null = null;

  // ── Login prompt state ─────────────────────────────────────────────────────
  showLoginPrompt = false;
  loginPromptUserName = '';
  loginPromptAction = '';

  // ── Blog action menu (3-dot) ───────────────────────────────────────────────
  activeActionBlogId: string | null = null;
  showDeleteBlogConfirm = false;
  private blogToDelete: IBlogVM | null = null;

  // ── Report blog modal ────────────────────────────────────────────────────
  isReportModalOpen = false;
  isSubmittingReport = false;
  reportError = '';
  reportSuccessOpen = false;
  private blogToReport: IBlogVM | null = null;

  // ── Block user (safeword-confirm) modal ─────────────────────────────────
  showBlockUserConfirm = false;
  private blogToBlock: IBlogVM | null = null;

  /** Bound to <app-block-user-confirm>'s [targetName] — "You're about to
   *  block {name}". */
  get blogToBlockDisplayName(): string {
    return this.blogToBlock?.author ?? '';
  }
  private reportService = inject(ReportService);

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private blogService: BlogService,
    // private soundService: ReactionSoundService,
    private router: Router,
    private searchService: SearchService,
  ) { }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    if (this.mixedMode) {
      this.applyMixedItems();
      return;
    }

    this.fetchBlogs();

    this.refreshSub = this.blogService.onRefresh$.subscribe(() => {
      this.page = 1;
      this.blogs = [];
      this.allBlogs = [];
      this.fetchBlogs();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.mixedMode) {
      if (changes['mixedItems']) {
        this.applyMixedItems();
      }
      return;
    }
    if (changes['userName'] && !changes['userName'].firstChange) {
      this.page = 1;
      this.blogs = [];
      this.allBlogs = [];
      this.fetchBlogs();
    }
    if (changes['collabOnly'] && !changes['collabOnly'].firstChange) {
      this.page = 1;
      this.blogs = [];
      this.allBlogs = [];
      this.fetchBlogs();
    }
    if ((changes['searchQuery'] && !changes['searchQuery'].firstChange) ||
        (changes['searchHashtag'] && !changes['searchHashtag'].firstChange)) {
      this.page = 1;
      this.blogs = [];
      this.allBlogs = [];
      this.fetchBlogs();
    }
  }

  /** Renders items handed down by the "All" page mixed feed — no HTTP call. */
  private applyMixedItems(): void {
    this.allBlogs = (this.mixedItems ?? []).map(b => this.toVM(b));
    this.applyStatusFilter();
    this.total = this.allBlogs.length;
    this.isLoading = false;
    this.cdr.detectChanges();
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        requestAnimationFrame(() => requestAnimationFrame(() => this.spanAllCards()));
      });
    }
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Per-card ResizeObserver: catches ANY late height change on a card — a
    // video whose metadata/dimensions resolve after the initial layout pass,
    // a slow image on a poor connection, avatar/hashtag wrapping, etc. — and
    // re-spans just that card the moment it actually happens, regardless of
    // how slow (or fast) the user's connection is. This is safe in mixedMode
    // too (unlike the document-body observer below) because it only reacts
    // to that one card's own content box, never to overall document height,
    // so it can't feed back into the other components mounted alongside it.
    this.cardResizeObserver = new ResizeObserver(entries => {
      this.zone.runOutsideAngular(() => {
        for (const entry of entries) {
          const card = entry.target as HTMLElement;
          requestAnimationFrame(() => this.spanCard(card));
        }
      });
    });
    this.observeCardsForResize();

    this.cardChangeSub = this.cardRefs.changes.subscribe(() => {
      this.observeCardsForResize();
      this.zone.runOutsideAngular(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.spanAllCards());
        });
      });
    });

    // Skipped in mixedMode: on the "All" page, Posts/Blog/Events are mounted
    // together, and each respanning its cards changes document height — with
    // three document-level observers that becomes a feedback loop (each one's
    // respan re-triggers the other two), pegging the CPU and hanging the tab.
    // cardChangeSub + onImageLoad already keep individual cards sized correctly.
    if (!this.mixedMode) {
      this.resizeObserver = new ResizeObserver(() => {
        this.zone.runOutsideAngular(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => this.spanAllCards());
          });
        });
      });
      this.resizeObserver.observe(document.body);
    }

    [50, 300, 800].forEach(delay => {
      setTimeout(() => {
        if (this.cardRefs?.length) {
          this.zone.runOutsideAngular(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => this.spanAllCards());
            });
          });
        }
      }, delay);
    });

    if (!this.mixedMode) setTimeout(() => this.setupScrollObserver(), 50);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.cardResizeObserver?.disconnect();
    this.scrollObserver?.disconnect();
    this.cardChangeSub?.unsubscribe();
    this.refreshSub?.unsubscribe();
    if (this.scrollCooldownTimer) clearTimeout(this.scrollCooldownTimer);
  }

  /** Re-attaches the card ResizeObserver to exactly the currently-rendered
   *  cards. Called on init and whenever cardRefs changes (blogs added/
   *  removed), so newly-rendered cards get observed and stale ones are
   *  dropped rather than accumulating stale observations. */
  private observeCardsForResize(): void {
    if (!this.cardResizeObserver || !this.cardRefs) return;
    this.cardResizeObserver.disconnect();
    this.cardRefs.forEach(ref => this.cardResizeObserver!.observe(ref.nativeElement));
  }

  // ── Scroll observer ────────────────────────────────────────────────────────

  private setupScrollObserver(): void {
    if (!this.sentinelRef) return;
    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isLoading && this.hasMore) {
          this.zone.run(() => this.loadNextPage());
        }
      },
      { threshold: 0 },
    );
    this.scrollObserver.observe(this.sentinelRef.nativeElement);
  }

  loadNextPage(): void {
    if (this.isLoading || !this.hasMore || this.scrollCooldownActive) return;
    this.page++;
    this.fetchBlogs();
  }

  /** See posts.ts for why this cooldown exists: masonry cards use `overflow:
   *  visible`, so the grid's measured height can briefly undercount (e.g. an
   *  image whose dimensions haven't loaded yet), which can keep the scroll
   *  sentinel "intersecting" and trigger a rapid-fire fetch loop. */
  private scrollCooldownActive = false;
  private scrollCooldownTimer?: ReturnType<typeof setTimeout>;
  private startScrollCooldown(): void {
    this.scrollCooldownActive = true;
    if (this.scrollCooldownTimer) clearTimeout(this.scrollCooldownTimer);
    this.scrollCooldownTimer = setTimeout(() => { this.scrollCooldownActive = false; }, 400);
  }

  // ── Fetch ─────────────────────────────────────────────────────────────────

  fetchBlogs(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.hasError = false;

    const isSearchMode = this.searchQuery !== undefined || this.searchHashtag !== undefined;

    const request$ = isSearchMode
      ? this.searchService.search(this.page, this.limit, {
          q: this.searchQuery, hashtag: this.searchHashtag, type: 'BLOG',
        })
      : this.favouritesMode
        ? this.favouriteService.getFavourites(this.page, this.limit, 'BLOG')
        : this.featured
          ? this.blogService.getFeaturedBlogs()
          : this.userName
            ? this.blogService.getUserBlogs(this.userName, this.page, this.limit, this.collabOnly)
            : this.blogService.getBlogs(this.page, this.limit);

    request$.subscribe({
      next: (res: any) => {
        this.zone.run(() => {
          const raw: IBlog[] = res?.data?.items ?? (Array.isArray(res?.data) ? res.data : []);
          const mapped = raw.map(b => this.toVM(b));

          if (this.featured) {
            this.allBlogs = mapped;
          } else {
            this.allBlogs = this.page === 1 ? mapped : [...this.allBlogs, ...mapped];
          }
          this.applyStatusFilter();
          this.total = (this.featured || this.favouritesMode) ? (res?.data?.total ?? mapped.length) : (res?.data?.total ?? 0);
          this.isLoading = false;
          this.startScrollCooldown();

          // markForCheck (not detectChanges): this callback can run while an
          // outer change-detection pass for this component is still unwinding
          // (e.g. embedded on the profile page), and a forced synchronous
          // re-check there trips NG0100 since blogs went from '[]' to populated
          // mid-check. markForCheck defers to the next tick instead.
          this.cdr.markForCheck();

          this.zone.runOutsideAngular(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => this.spanAllCards());
            });
          });
        });
      },
      error: () => {
        this.zone.run(() => {
          this.isLoading = false;
          this.hasError = true;
        });
      },
    });
  }

  /** Single source of truth for the ui-avatars fallback used across this page. */
  avatarFallback(name?: string | null): string {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFD4E0&color=D31148`;
  }

  // ── Map IBlog → IBlogVM ───────────────────────────────────────────────────

  private toVM(blog: IBlog): IBlogVM {
    const blocks = (blog.blogInfo?.blocks ?? [])
      .slice()
      .sort((a, b) => a.position - b.position);

    const mediaBlocks = blocks.filter(b => b.type === 'IMAGE' || b.type === 'VIDEO');

    const images: string[] = mediaBlocks.map(b =>
      b.mediaId?.urlMedium ?? b.mediaId?.url ?? '',
    ).filter(Boolean);

    const aspectMap: Record<number, string> = {
      0: '4 / 3', 1: '4 / 3', 2: '16 / 9', 3: '3 / 4',
    };
    const imageAspect = aspectMap[Math.min(images.length, 3)] ?? '4 / 3';

    const firstText = blocks.find(b => b.type === 'TEXT');
    const rawText = firstText?.text ? this.stripHtml(firstText.text) : '';
    const LIMIT = 200;
    const text = rawText.length > LIMIT
      ? rawText.slice(0, LIMIT).trimEnd() + '…'
      : rawText;

    const avatar = blog.user?.profileImage?.urlSmall
      ?? blog.user?.profileImage?.urlMedium
      ?? this.avatarFallback(blog.user?.displayName);

    const reactionCounts = blog.reactions?.counts ?? {
      LIKE: 0, HEART: 0, HUG: 0, WOW: 0, SAD: 0, HAHA: 0,
    };
    const totalReactions = blog.reactions?.total ?? blog.numberOfLikes ?? 0;
    const acceptedCollaborators = (blog.blogInfo?.collaborations ?? [])
      .filter(collab => collab.status === 'ACCEPTED')
      .map(collab => ({
        _id: collab.user?._id ?? '',
        displayName: collab.user?.displayName ?? '',
        userName: collab.user?.userName ?? '',
        profileImage: collab.user?.profileImage ?? undefined,
      }));
    const mentions = (blog.blogInfo?.mentions ?? []).map(u => ({
      _id: u._id ?? '',
      displayName: u.displayName ?? '',
      userName: u.userName ?? '',
      profileImage: u.profileImage ?? undefined,
    }));

    return {
      _id: blog._id,
      userId: blog.user?._id ?? '',
      avatar,
      author: blog.user?.userName ?? '',
      title: blog.title,
      timeAgo: this.timeAgo(blog.createdAt),
      images,
      mediaBlocks,
      activeSlide: 0,
      imageAspect,
      text,
      hasMore: rawText.length > LIMIT,
      likes: totalReactions,
      comments: blog.numberOfComments ?? 0,
      shares: blog.numberOfShares ?? 0,
      myReaction: blog.reactions?.myReaction ?? null,
      reactions: totalReactions,
      totalReactions,
      reactionCounts: { ...reactionCounts },
      recentUserName: blog.reactions?.recentUserName ?? null,
      topTypes: (blog.reactions?.topTypes ?? []) as ReactionType[],
      showEmojiPicker: false,
      hoveredReaction: null,
      isFollowed: blog.isFollowed,
      isMyBlog: blog.isMyBlog,
      status: (blog.blogInfo?.status ?? 'PUBLISHED') as 'DRAFT' | 'PUBLISHED',
      commentsDisabled: blog.blogInfo?.commentsDisabled ?? false,
      friendshipStatus: (blog.friendship?.status as FriendshipStatus) ?? 'NONE',
      friendshipRequestId: blog.friendship?.requestId ?? null,
      isFavourite: (blog as any).isFavourite ?? false,
      globalOrder: (blog as any)._order,
      acceptedCollaborators,
      mentions,
      isRemovedByAdmin: (blog as any).isRemovedByAdmin ?? false,
    };
  }

  // ── Status filter (profile blog tab) ─────────────────────────────────────

  setStatusFilter(filter: 'ALL' | 'PUBLISHED' | 'DRAFT'): void {
    this.statusFilter = filter;
    this.applyStatusFilter();
    this.cdr.detectChanges();
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => this.spanAllCards()));
    });
  }

  private applyStatusFilter(): void {
    if (this.statusFilter === 'ALL') {
      this.blogs = [...this.allBlogs];
    } else {
      this.blogs = this.allBlogs.filter(b => b.status === this.statusFilter);
    }
  }

  get publishedCount(): number { return this.allBlogs.filter(b => b.status === 'PUBLISHED').length; }
  get draftCount(): number     { return this.allBlogs.filter(b => b.status === 'DRAFT').length; }

  // ── Navigation ────────────────────────────────────────────────────────────

  navigateToBlog(blog: IBlogVM): void {
    if (blog.isMyBlog && blog.status === 'DRAFT') {
      this.router.navigate(['/blog/create'], { queryParams: { edit: blog._id } });
    } else {
      this.router.navigate(['/blog', blog._id], {
        state: { returnUrl: this.router.url }
      });
    }
  }

  editDraft(event: MouseEvent, blog: IBlogVM): void {
    event.stopPropagation();
    this.router.navigate(['/blog/create'], { queryParams: { edit: blog._id } });
  }

  // ── Carousel ──────────────────────────────────────────────────────────────

  goToSlide(blog: IBlogVM, index: number): void {
    blog.activeSlide = index;
    this.respanAfterSlide();
  }

  prevSlide(blog: IBlogVM, event: Event): void {
    event.stopPropagation();
    blog.activeSlide = blog.activeSlide === 0 ? blog.images.length - 1 : blog.activeSlide - 1;
    this.respanAfterSlide();
  }

  nextSlide(blog: IBlogVM, event: Event): void {
    event.stopPropagation();
    blog.activeSlide = blog.activeSlide === blog.images.length - 1 ? 0 : blog.activeSlide + 1;
    this.respanAfterSlide();
  }

  /** Touch swipe on the card carousel. `dir` is +1 for next, -1 for previous. */
  onSlideSwipe(blog: IBlogVM, dir: 1 | -1): void {
    const total = blog.images.length;
    if (total <= 1) return;
    blog.activeSlide = (blog.activeSlide + dir + total) % total;
    this.cdr.detectChanges();
    this.respanAfterSlide();
  }

  private respanAfterSlide(): void {
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.spanAllCards());
      });
    });
  }

  isVideoSlide(blog: IBlogVM, index: number): boolean {
    return blog.mediaBlocks[index]?.type === 'VIDEO';
  }

  // ── Reactions ─────────────────────────────────────────────────────────────

  toggleLike(blog: IBlogVM): void {
    this.blogService.reactToBlog(blog._id, 'LIKE').subscribe({
      next: (res: any) => {
        const data = res?.data;
        if (data) {
          blog.myReaction = data.reactions?.myReaction ?? null;
          const total = data.reactions?.total ?? data.numberOfLikes ?? blog.totalReactions ?? blog.likes;
          blog.totalReactions = total;
          blog.reactions = total;
          blog.likes = total;
          if (data.reactions?.counts) {
            blog.reactionCounts = { ...data.reactions.counts };
          }
          this.cdr.detectChanges();
        }
      },
    });
  }

  // ── Follow / Friend actions ───────────────────────────────────────────────

  public follow(event: MouseEvent, card: IBlogVM): void {
    event.stopPropagation();
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(card.author, 'follow');
      return;
    }
    if (!card.userId) return;
    card.isFollowed = true;
    this.cdr.detectChanges();
    this.relationshipService.follow(card.userId).subscribe({
      error: () => { card.isFollowed = false; this.cdr.detectChanges(); },
    });
  }

  public unfollow(event: MouseEvent, card: IBlogVM): void {
    event.stopPropagation();
    this.activeActionBlogId = null;
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(card.author, 'unfollow');
      return;
    }
    if (!card.userId) return;
    card.isFollowed = false;
    this.cdr.detectChanges();
    this.relationshipService.unfollow(card.userId).subscribe({
      error: () => { card.isFollowed = true; this.cdr.detectChanges(); },
    });
  }

  friendButtonTitle(status: FriendshipStatus): string {
    const map: Record<FriendshipStatus, string> = {
      NONE: 'Send friend request',
      PENDING_SENT: 'Cancel friend request',
      PENDING_RECEIVED: 'Accept friend request',
      FRIENDS: 'Unfriend',
      BLOCKED: 'Blocked',
    };
    return map[status];
  }

  onFriendButtonClick(card: IBlogVM, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(card.author, 'add as a friend');
      return;
    }
    switch (card.friendshipStatus) {
      case 'NONE': this.sendFriendRequest(card, event); break;
      case 'PENDING_SENT': this.cancelFriendRequest(card, event); break;
      case 'PENDING_RECEIVED': this.acceptFriendRequest(card, event); break;
      case 'FRIENDS': this.promptUnfriend(card, event); break;
      case 'BLOCKED': break;
    }
  }

  showUnfriendConfirm = false;
  private pendingUnfriendAction: (() => void) | null = null;

  private promptUnfriend(card: IBlogVM, event: MouseEvent): void {
    event.stopPropagation();
    this.pendingUnfriendAction = () => this.unfriend(card, event);
    this.showUnfriendConfirm = true;
  }

  confirmUnfriend(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction?.();
    this.pendingUnfriendAction = null;
  }

  cancelUnfriendConfirm(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction = null;
  }

  /** They've sent you a request — clicking the friend icon accepts it. (It
   *  used to call sendFriendRequest, which bails unless the status is NONE,
   *  so the button did nothing — SAF-839.) */
  private acceptFriendRequest(card: IBlogVM, event: MouseEvent): void {
    event.stopPropagation();
    if (card.friendshipStatus !== 'PENDING_RECEIVED' || !card.friendshipRequestId) return;
    card.friendshipStatus = 'FRIENDS';
    this.cdr.detectChanges();
    this.friendRequestService.accept(card.friendshipRequestId, card.userId).subscribe({
      error: () => { card.friendshipStatus = 'PENDING_RECEIVED'; this.cdr.detectChanges(); },
    });
  }

  private sendFriendRequest(card: IBlogVM, event: MouseEvent): void {
    event.stopPropagation();
    if (card.friendshipStatus !== 'NONE' || !card.userId) return;
    card.friendshipStatus = 'PENDING_SENT';
    this.cdr.detectChanges();
    this.friendRequestService.send(card.userId).subscribe({
      next: (res: any) => {
        card.friendshipRequestId = res?.data?._id ?? null;
        this.cdr.detectChanges();
      },
      error: (err) => {
        const status = err?.error?.statusCode ?? err?.status;
        if (status !== 409) { card.friendshipStatus = 'NONE'; }
        const cooldownHours = this.friendRequestService.parseCooldownError(err);
        if (cooldownHours !== null) {
          this.friendLimitHours = cooldownHours;
          this.showFriendLimitPopup = true;
        }
        this.cdr.detectChanges();
      },
    });
  }

  private cancelFriendRequest(card: IBlogVM, event: MouseEvent): void {
    event.stopPropagation();
    if (!card.friendshipRequestId) return;
    const prevStatus = card.friendshipStatus;
    const prevRequestId = card.friendshipRequestId;
    card.friendshipStatus = 'NONE';
    this.cdr.detectChanges();
    this.friendRequestService.cancel(card.friendshipRequestId).subscribe({
      error: () => {
        card.friendshipStatus = prevStatus;
        card.friendshipRequestId = prevRequestId;
        this.cdr.detectChanges();
      },
    });
  }

  private unfriend(card: IBlogVM, event: MouseEvent): void {
    event.stopPropagation();
    if (!card.userId) return;
    const prevStatus = card.friendshipStatus;
    const prevRequestId = card.friendshipRequestId;
    card.friendshipStatus = 'NONE';
    card.friendshipRequestId = null;
    this.cdr.detectChanges();
    this.friendRequestService.unfriend(card.userId).subscribe({
      error: () => {
        card.friendshipStatus = prevStatus;
        card.friendshipRequestId = prevRequestId;
        this.cdr.detectChanges();
      },
    });
  }

  // ── Reactions (picker + modal) ────────────────────────────────────────────

  getReactionConfig(type: ReactionType | null): typeof REACTIONS[0] | null {
    if (!type) return null;
    return REACTIONS.find(r => r.type === type) ?? null;
  }

  onReactionClick(card: IBlogVM): void {
    if (!this.isLoggedIn()) {
      const collaboratorNames = (card.acceptedCollaborators ?? [])
        .map((c: any) => c.userName)
        .join(', ');
      const displayName = collaboratorNames
        ? `${card.author} & ${collaboratorNames}`
        : card.author;
      this.openLoginPrompt(displayName, 'react to this post by');
      return;
    }
    if (card.showEmojiPicker) {
      card.showEmojiPicker = false;
    } else {
      // this.soundService.playPickerOpen();
      this.blogs.forEach(b => b.showEmojiPicker = false);
      card.showEmojiPicker = true;
    }
    this.cdr.detectChanges();
  }

  onEmojiPick(card: IBlogVM, type: ReactionType, $event: MouseEvent): void {
    $event.stopPropagation();
    card.showEmojiPicker = false;
    card.hoveredReaction = null;
    // this.soundService.playReactionSelect(type);
    this.submitReaction(card, type);
  }

  private submitReaction(card: IBlogVM, type: ReactionType): void {
    const previous = card.myReaction;
    const previousTopTypes = [...card.topTypes];
    const previousRecentUserName = card.recentUserName;

    if (previous === type) {
      card.reactionCounts[type] = Math.max(0, card.reactionCounts[type] - 1);
      card.totalReactions = Math.max(0, card.totalReactions - 1);
      card.myReaction = null;
    } else {
      if (previous) {
        card.reactionCounts[previous as ReactionType] =
          Math.max(0, card.reactionCounts[previous as ReactionType] - 1);
        card.totalReactions = Math.max(0, card.totalReactions - 1);
      }
      card.reactionCounts[type]++;
      card.totalReactions++;
      card.myReaction = type;
    }
    card.reactions = card.totalReactions;
    card.likes = card.totalReactions;

    // Update topTypes and recentUserName immediately
    card.topTypes = this.computeTopTypes(card.reactionCounts);
    const currentUserName = this.authState.getAuthSnapshot()?.user?.userName ?? null;
    if (card.myReaction) {
      card.recentUserName = currentUserName;
    } else if (card.totalReactions === 0) {
      card.recentUserName = null;
    }

    this.cdr.detectChanges();

    this.blogService.reactToBlog(card._id, type).subscribe({
      error: () => {
        card.myReaction = previous;
        card.reactionCounts = { ...card.reactionCounts };
        card.totalReactions = Object.values(card.reactionCounts).reduce((a, b) => a + b, 0);
        card.reactions = card.totalReactions;
        card.likes = card.totalReactions;
        card.topTypes = previousTopTypes;
        card.recentUserName = previousRecentUserName;
        this.cdr.detectChanges();
      },
    });
  }

  private computeTopTypes(counts: ReactionCounts): ReactionType[] {
    return (Object.entries(counts) as [ReactionType, number][])
      .filter(([, count]) => count > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([type]) => type);
  }

  openReactionModal(card: IBlogVM): void {
    this.isReactionOpen = true;
    this.activeReactionFilter = null;
    this.activeReactionEventId = card._id;
    this.loadReactions(card._id);
  }

  closeReactionModal(): void {
    this.isReactionOpen = false;
    this.reactionData = null;
    this.activeReactionEventId = null;
  }

  closeEmojiPicker(card: IBlogVM): void {
    card.showEmojiPicker = false;
    card.hoveredReaction = null;
    this.cdr.detectChanges();
  }

  setReactionFilter(type: ReactionType | null): void {
    this.activeReactionFilter = type;
    if (this.activeReactionEventId) {
      this.loadReactions(this.activeReactionEventId, type ?? undefined);
    }
  }

  private loadReactions(eventId: string, type?: ReactionType): void {
    this.reactionsLoading = true;
    this.cdr.detectChanges();
    this.blogService.getReactions(eventId, type).subscribe({
      next: (res: any) => {
        this.reactionData = res?.data;
        this.reactionsLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.reactionsLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  get filteredReactions(): any[] { return this.reactionData?.all ?? []; }

  showCollaboratorsModal(blog: IBlogVM): void {
    this.selectedBlog = blog;
    this.showCollabModal = true;
  }

  closeCollaboratorsModal(): void {
    this.showCollabModal = false;
    this.selectedBlog = null;
  }

  showTaggedPeopleModal(blog: IBlogVM, event?: Event): void {
    event?.stopPropagation();
    this.selectedBlog = blog;
    this.showTaggedModal = true;
  }

  closeTaggedPeopleModal(): void {
    this.showTaggedModal = false;
    this.selectedBlog = null;
  }

  // ── Masonry ───────────────────────────────────────────────────────────────

  onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    const card = img.closest<HTMLElement>('.blog-masonry-item');
    if (!card) return;
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.spanCard(card));
      });
    });
  }

  private spanAllCards(): void {
    if (!this.cardRefs) return;
    this.cardRefs.forEach(ref => this.spanCard(ref.nativeElement));
  }

  private spanCard(card: HTMLElement): void {
    if (this.uniform) { card.style.gridRowEnd = ''; return; } // equal-height grid: no masonry row spans
    const rowUnit = 10;
    const rowGap = 20;
    const height = card.getBoundingClientRect().height;

    if (height === 0) {
      setTimeout(() => {
        const h2 = card.getBoundingClientRect().height;
        if (h2 > 0) {
          card.style.gridRowEnd = `span ${Math.ceil((h2 + rowGap) / rowUnit)}`;
        }
      }, 100);
      return;
    }

    card.style.gridRowEnd = `span ${Math.ceil((height + rowGap) / rowUnit)}`;
  }

  // ── Template helpers ──────────────────────────────────────────────────────

  trackById(_: number, blog: IBlogVM): string { return blog._id; }
  trackByIndex(index: number): number { return index; }

  get hasMore(): boolean { return this.blogs.length < this.total; }

  // ── Utilities ─────────────────────────────────────────────────────────────

  /**
   * Plain-text preview for card snippets. Uses the same tag-to-space regex on
   * both server and client (rather than DOM textContent in the browser) —
   * textContent doesn't insert whitespace at tag boundaries, so block markup
   * like a pasted <table> collapses into one run-together word (e.g. every
   * <td> in a row glued to the next with no space). It would also produce
   * different output server- vs client-side, which is its own hydration risk.
   */
  private stripHtml(html: string): string {
    return html
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }

  private timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m} minute${m > 1 ? 's' : ''} ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hour${h > 1 ? 's' : ''} ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d} day${d > 1 ? 's' : ''} ago`;
    const w = Math.floor(d / 7);
    if (w < 5) return `${w} week${w > 1 ? 's' : ''} ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo} month${mo > 1 ? 's' : ''} ago`;
    return `${Math.floor(mo / 12)} year${Math.floor(mo / 12) > 1 ? 's' : ''} ago`;
  }

  onModalFollowClick(item: any, event: MouseEvent): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(item.user?.userName ?? item.user?.userName, 'follow');
      return;
    }
    event.stopPropagation();
    if (item.isFollowed) {
      item.isFollowed = false;
      this.cdr.markForCheck();
      this.relationshipService.unfollow(item.user?._id).subscribe({
        error: () => { item.isFollowed = true; this.cdr.markForCheck(); }
      });
    } else {
      item.isFollowed = true;
      this.cdr.markForCheck();
      this.relationshipService.follow(item.user?._id).subscribe({
        error: () => { item.isFollowed = false; this.cdr.markForCheck(); }
      });
    }
  }

  onModalFriendClick(item: any, event: MouseEvent): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(item.user?.userName ?? item.user?.userName, 'add as a friend');
      return;
    }
    event.stopPropagation();
    const status = item.friendship.status ?? 'NONE';

    if (status === 'NONE') {
      item.friendship.status = 'PENDING_SENT';
      this.cdr.markForCheck();
      this.friendRequestService.send(item.user?._id).subscribe({
        next: (res: any) => { item.friendship.requestId = res?.data?._id ?? null; this.cdr.markForCheck(); },
        error: (err: any) => {
          const code = err?.error?.statusCode ?? err?.status;
          if (code !== 409) item.friendship.status = 'NONE';
          const cooldownHours = this.friendRequestService.parseCooldownError(err);
          if (cooldownHours !== null) {
            this.friendLimitHours = cooldownHours;
            this.showFriendLimitPopup = true;
          }
          this.cdr.markForCheck();
        }
      });
    } else if (status === 'PENDING_SENT') {
      const prev = item.friendship.status;
      const prevId = item.friendship.requestId;
      item.friendship.status = 'NONE';
      this.cdr.markForCheck();
      this.friendRequestService.cancel(item.friendship.requestId).subscribe({
        error: () => { item.friendship.status = prev; item.friendship.requestId = prevId; this.cdr.markForCheck(); }
      });
    } else if (status === 'FRIENDS') {
      this.pendingUnfriendAction = () => {
        const prev = item.friendship.status;
        item.friendship.status = 'NONE';
        this.cdr.markForCheck();
        this.friendRequestService.unfriend(item.user?._id).subscribe({
          error: () => { item.friendship.status = prev; this.cdr.markForCheck(); }
        });
      };
      this.showUnfriendConfirm = true;
    }
  }


  private isLoggedIn(): boolean {
    return this.authState.getAuthSnapshot()?.isLoggedIn === true;
  }

  private openLoginPrompt(userName: string, action: string): void {
    this.loginPromptUserName = userName;
    this.loginPromptAction = action;
    this.showLoginPrompt = true;
    this.cdr.detectChanges();
  }

  closeLoginPrompt(): void {
    this.showLoginPrompt = false;
    this.cdr.detectChanges();
  }

  goToLogin(): void {
    this.closeLoginPrompt();
    this.router.navigate(['/login']);
  }

  // ── Share ────────────────────────────────────────────────────────────────────

  openShareSheet(blog: any, event: MouseEvent): void {
    event.stopPropagation();
    this.activeShareRef = blog;
    this.activeShareCount = blog.shares ?? 0;
    this.activeShareItem = {
      contentId: blog._id,
      contentType: 'blog',
      title: blog.title ?? 'Check out this blog',
      description: blog.excerpt ?? blog.title,
      imageUrl: blog.coverImage,
    };
    this.cdr.detectChanges();
  }

  closeShareSheet(): void {
    this.activeShareItem = null;
    this.activeShareRef = null;
    this.cdr.detectChanges();
  }

  onShareCountUpdated(count: number): void {
    if (this.activeShareRef) this.activeShareRef.shares = count;
    this.cdr.detectChanges();
  }

  // ── Favourites ─────────────────────────────────────────────────────────────

  toggleFavourite(blog: IBlogVM, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(blog.author, 'save to favourites');
      return;
    }
    const prev = blog.isFavourite;
    blog.isFavourite = !prev;
    this.cdr.detectChanges();
    this.favouriteService.toggle(blog._id).subscribe({
      next: (res: any) => {
        blog.isFavourite = res?.data?.isFavourite ?? !prev;
        this.favouriteService.broadcastToggle(blog._id, blog.isFavourite);
        this.cdr.detectChanges();
      },
      error: () => { blog.isFavourite = prev; this.cdr.detectChanges(); },
    });
  }

  // ── Blog action menu (3-dot) ───────────────────────────────────────────────

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.activeActionBlogId !== null) {
      this.activeActionBlogId = null;
      this.cdr.detectChanges();
    }
  }

  toggleBlogActionMenu(blog: IBlogVM, event: MouseEvent): void {
    event.stopPropagation();
    this.activeActionBlogId = this.activeActionBlogId === blog._id ? null : blog._id;
    this.cdr.detectChanges();
  }

  /** Blog whose comments on/off request is in flight (disables its menu item). */
  togglingCommentsId: string | null = null;

  /** Turn comments on/off from the card's 3-dot menu — same API as blog-details. */
  toggleComments(blog: IBlogVM, event: MouseEvent): void {
    event.stopPropagation();
    if (this.togglingCommentsId) return;
    this.togglingCommentsId = blog._id;
    this.blogService.toggleComments(blog._id).subscribe({
      next: (res: any) => {
        blog.commentsDisabled = res.data?.blogInfo?.commentsDisabled ?? !blog.commentsDisabled;
        this.togglingCommentsId = null;
        this.activeActionBlogId = null;
        this.cdr.detectChanges();
      },
      error: () => {
        this.togglingCommentsId = null;
        this.cdr.detectChanges();
      },
    });
  }

  editBlog(blog: IBlogVM, event: MouseEvent): void {
    event.stopPropagation();
    this.activeActionBlogId = null;
    if (blog.status === 'DRAFT') {
      this.router.navigate(['/blog/create'], { queryParams: { edit: blog._id } });
    } else {
      this.router.navigate(['/blog/create'], { queryParams: { edit: blog._id } });
    }
    this.cdr.detectChanges();
  }

  confirmDeleteBlog(blog: IBlogVM, event: MouseEvent): void {
    event.stopPropagation();
    this.activeActionBlogId = null;
    this.blogToDelete = blog;
    this.showDeleteBlogConfirm = true;
    this.cdr.detectChanges();
  }

  cancelDeleteBlog(): void {
    this.showDeleteBlogConfirm = false;
    this.blogToDelete = null;
    this.cdr.detectChanges();
  }

  doDeleteBlog(): void {
    if (!this.blogToDelete) return;
    const blog = this.blogToDelete;
    this.showDeleteBlogConfirm = false;
    this.blogToDelete = null;
    this.blogService.deleteBlog(blog._id).subscribe({
      next: () => {
        this.allBlogs = this.allBlogs.filter(b => b._id !== blog._id);
        this.applyStatusFilter();
        this.blogService.triggerRefresh();
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error deleting blog:', err);
        this.cdr.detectChanges();
      }
    });
  }

  reportBlog(blog: IBlogVM, event: MouseEvent): void {
    event.stopPropagation();
    this.activeActionBlogId = null;
    this.blogToReport = blog;
    this.reportError = '';
    this.isReportModalOpen = true;
    this.cdr.detectChanges();
  }

  closeReportModal(): void {
    if (this.isSubmittingReport) return;
    this.isReportModalOpen = false;
    this.blogToReport = null;
    this.reportError = '';
    this.cdr.detectChanges();
  }

  submitBlogReport(description: string): void {
    if (!this.blogToReport) return;
    this.isSubmittingReport = true;
    this.reportError = '';

    this.reportService.submitReport(this.blogToReport._id, description).subscribe({
      next: () => {
        this.zone.run(() => {
          this.isSubmittingReport = false;
          this.isReportModalOpen = false;
          this.blogToReport = null;
          this.reportSuccessOpen = true;
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        this.zone.run(() => {
          this.isSubmittingReport = false;
          this.reportError = err?.error?.message ?? 'Failed to submit report. Please try again.';
          this.cdr.detectChanges();
        });
      },
    });
  }

  closeReportSuccess(): void {
    this.reportSuccessOpen = false;
    this.cdr.detectChanges();
  }

  // ── Block user (from a blog's 3-dot menu) ───────────────────────────────

  blockBlogUser(blog: IBlogVM, event: MouseEvent): void {
    event.stopPropagation();
    this.activeActionBlogId = null;
    this.blogToBlock = blog;
    this.showBlockUserConfirm = true;
    this.cdr.detectChanges();
  }

  cancelBlockBlogUser(): void {
    this.showBlockUserConfirm = false;
    this.blogToBlock = null;
    this.cdr.detectChanges();
  }

  confirmBlockBlogUser(): void {
    this.showBlockUserConfirm = false;
    const blog = this.blogToBlock;
    this.blogToBlock = null;
    if (!blog?.userId) return;

    // Optimistically drop every blog from this author out of the current
    // list — mirrors what a fresh feed fetch would return once blocked
    // users' content is filtered out server-side.
    const prevAllBlogs = this.allBlogs;
    this.allBlogs = this.allBlogs.filter(b => b.userId !== blog.userId);
    this.applyStatusFilter();
    this.cdr.detectChanges();

    this.friendRequestService.block(blog.userId).subscribe({
      next: () => this.friendRequestService.triggerRefresh(),
      error: (err) => {
        console.error('Block user failed:', err);
        this.allBlogs = prevAllBlogs;
        this.applyStatusFilter();
        this.cdr.detectChanges();
      },
    });
  }
}